/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { Box, Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import moment from 'moment'
import React, { useEffect } from 'react'
import { useAsyncHandler, useAsyncIntervalFetch } from 'react-hooks-async-handlers'
import { useSelector } from 'react-redux'
import { StepComponentProps } from 'react-step-builder'
import { useLocalStorage } from 'usehooks-ts'
import config from '../../config'
import analytics from '../../libs/analytics'
import api from '../../libs/api'
import { ReduxState } from '../../reducers'
import waitingIcon from './assets/bubble-gum-downloading.gif'
import appIcon from './assets/employeeAppIcon.png'
import NeedHelp from './components/NeedHelp'
import PhonePreview from './components/PhonePreview'
import SendSmsAgainForm from './phone/SendSmsAgainForm'

const POLING_INTERVAL = 5000 // m.sec.

function StepLogin(props: StepComponentProps) {
  const [smsDate, setSmsDate] = useLocalStorage('setting:sms-time', '')
  const retryMoment = smsDate ? moment(smsDate) : undefined

  const { phoneInfo, phone_number } = useSelector((state: ReduxState) => ({
    phoneInfo: state.phone,
    phone_number: state.form.phone_number,
  }))

  const actionSendSmsAgain = useAsyncHandler(async () => {
    analytics.sendEvent('Onboard@SendSMS', {}, { phone_number })
    await api.sendSmsAgain({ phone_number })
    setSmsDate(moment().add(1, 'minute').toISOString())
  })

  const isDifferentFlow = !!phoneInfo.verification_code // tmp workaround until we didn't migrate to better router

  useAsyncIntervalFetch(async () => {
    if (document.hidden) return null // no need to spam on background
    if (isDifferentFlow) return null
    const { data } = await api.onboardCheckProgressCreate({ phone_number })
    if (data?.redirect) {
      /**
       * Here we need to wait when user will login from mobile side.
       * When it will happen we will receive redirect=true from server.
       * It means onboard is completed.
       */
      window.parent.postMessage(`track---newHRAccount`, config.cmsFrameLink)

      props.jump(8) // should navigate to StepEnd
      // navigationUtil.navigateToHrLogin(props.state.email as string)
    }
  }, POLING_INTERVAL)

  useEffect(() => {
    const identity_verification_code = phoneInfo.verification_code
    if (identity_verification_code) {
      /**
       * Identity verification flow should be on different screen. So we need to skip this screen somehow.
       * It is just workaround for bad navigation design
       */
      props.next()
    } else {
      analytics.sendEvent('page@StepLogin')
    }
  }, [])

  return (
    <Grid container direction="column" alignItems="center">
      <Typography sx={{ fontSize: '22px', color: '#352E73', textAlign: 'center', mt: 2, fontWeight: 600 }}>
        We sent a text message with your Reaction Club app link to...
      </Typography>

      <PhonePreview phone_number={phone_number} onRequestChange={props.prev} />

      <div>
        <Grid container alignItems={'center'} direction={'column'}>
          <div>
            {/* component sends sms automatically */}
            {isDifferentFlow ? null : (
              <SendSmsAgainForm
                retryMoment={retryMoment}
                onClickSend={actionSendSmsAgain.execute}
                onRetryReset={() => setSmsDate('')}
              />
            )}
          </div>

          {actionSendSmsAgain.error ? <Typography color={'error'}>{actionSendSmsAgain.error}</Typography> : null}
        </Grid>
      </div>

      <div>
        <img
          src={waitingIcon}
          alt="waiting for app login"
          css={css`
            width: 300px;
            height: 170px;
            object-fit: cover;
          `}
        />
      </div>

      <Typography
        textAlign={'center'}
        fontWeight={'bold'}
        color={'secondary'}
        css={css`
          font-size: 22px;
          line-height: 33px;
          max-width: 510px;
          margin-bottom: 10px;
        `}
      >
        Next step - click the link in the text message to login to your Reaction Club app...
      </Typography>
      <Typography textAlign={'center'}>
        After installing the app, you'll be redirected to your dashboard automatically
      </Typography>

      <Grid container justifyContent={'center'} flexDirection={'column'} alignItems={'center'}>
        <Box sx={{ mt: 2, mb: 2 }}>
          <img
            src={appIcon}
            alt="App"
            css={css`
              width: 66px;
              height: 66px;
            `}
          />
        </Box>

        <Typography
          textAlign={'center'}
          css={css`
            font-size: 14px;
            line-height: 21px;
            max-width: 414px;
            margin-bottom: 20px;
          `}
        >
          If you didn’t receive a text download the Reaction Club app and login with the same phone number
        </Typography>

        <NeedHelp />

        {/* <Grid container direction={'column'} alignItems={'center'}> */}
        {/*  <Typography fontWeight={'bold'}>If you didn’t get a text</Typography> */}
        {/*  <Typography>1. visit the app store on your phone</Typography> */}
        {/*  <Typography>{`2. Download the "Reaction Club" app`}</Typography> */}
        {/*  <Typography>3. Login with the same phone number</Typography> */}
        {/* </Grid> */}
      </Grid>
    </Grid>
  )
}

export default StepLogin
